import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { IBreadcrumb } from '@d-models/other/breadcrumb.model';
import { checkIsActive } from '@d-utils/router.util';
import { MenuItem } from 'primeng/api';
import findIndex from 'lodash-es/findIndex';
import uniqBy from 'lodash-es/uniqBy';
import { cloneDeep } from 'lodash-es';
import { AppConstant } from '@d-constants';
import { MODULE_CODE_PERMISSION } from '@d-constants/permission.constant';
import { RoleService } from '@d-services';
import { AppState } from '@d-store/constant';
import { select, Store } from '@ngrx/store';
import { getCurrentUserLevel } from '@d-store/user/user.selector';
import { USER_LEVEL } from '@d-constants/user.constant';
import { map } from 'rxjs';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit {
  items: MenuItem[] = [];

  currentPageTitle!: string;
  currentLevel!: string;
  unmodifiedMenuItems: MenuItem[] = [];

  @Output() menuItemChanged: EventEmitter<IBreadcrumb> = new EventEmitter();

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private roleService: RoleService,
    private store: Store<AppState>,
  ) {
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.setMenuItems(this.displayTenantLevelMenu());
      // if (this.currentLevel === AppConstant.TENANT_LEVEL) {
      //   // this.setMenuItems(this.displayTenantLevelMenu());
      // } else if (this.currentLevel === AppConstant.PO_LEVEL) {
      //   // this.setMenuItems(this.displayPOLevelMenu());
      // }
    });

    router.events.subscribe((val: any) => {
      if (val instanceof NavigationEnd) {
        this.scanActiveRouter(this.items, val.urlAfterRedirects ?? val.url);
      }
    });

    this.setMenuItems(this.displayTenantLevelMenu());
    // this.store
    //   .pipe(
    //     select(getCurrentUserLevel),
    //     map((val) => val.level),
    //   )
    //   .subscribe((level) => {
    //     if (level === USER_LEVEL.TENANT_LEVEL) {
    //       this.setMenuItems(this.displayTenantLevelMenu());
    //     } else if (level === USER_LEVEL.PO_LEVEL) {
    //       this.setMenuItems(this.displayPOLevelMenu());
    //     }
    //   });
  }

  ngOnInit(): void {
    this.scanActiveRouter(this.items, this.router.url);
  }

  setMenuItems(items: MenuItem[]) {
    this.items = items;
    this.unmodifiedMenuItems = cloneDeep(items);
  }

  scanActiveRouter(items: MenuItem[], url: string) {
    items.forEach((i, index) => {
      if (i.items) {
        this.scanActiveRouter(i.items, url);
        const n = i.items.findIndex((i) => i.styleClass === 'active');
        i.expanded = n != -1;
        if (i.expanded) {
          // disable routerLink of active parent menu
          i.routerLink = null;
        } else {
          i.routerLink = this.unmodifiedMenuItems[index].routerLink;
        }
      } else {
        i.expanded = checkIsActive(url, i.routerLink);
      }
      i.styleClass = i.expanded ? 'active' : '';
    });
  }

  menuItemClick(event: any) {
    const breadcrumbItems: IBreadcrumb = { items: [], homeIcon: {} };
    this.currentPageTitle = '';
    this.unmodifiedMenuItems.forEach((element) => {
      const icon = element.icon + ' dialog-icon';
      if (element.label === event.item.label) {
        const parent: MenuItem = {
          label: element.label,
          routerLink: element.routerLink,
        };
        breadcrumbItems.items.push(parent);
        breadcrumbItems.homeIcon = {
          icon: icon,
        };
      }

      if (element.items && element.items.length > 0) {
        const childIndex = findIndex(element.items, (x) => {
          return x.label === event.item.label;
        });

        if (childIndex > -1 && element.items[childIndex].routerLink) {
          const parent: MenuItem = {
            label: element.label,
            routerLink: element.routerLink,
          };

          const children: MenuItem = {
            label: element.items[childIndex].label,
            routerLink: element.items[childIndex].routerLink,
          };

          breadcrumbItems.items.push(parent);
          breadcrumbItems.items.push(children);
          breadcrumbItems.homeIcon = {
            icon: icon,
          };
        }
      }
    });
    if (this.currentPageTitle) {
      breadcrumbItems.items.push({ label: this.currentPageTitle });
    }
    breadcrumbItems.items = uniqBy(breadcrumbItems.items, 'label');
    this.menuItemChanged.emit(breadcrumbItems);
  }

  displayTenantLevelMenu(): MenuItem[] {
    return [
      {
        label: this.translateService.instant('HOME'),
        icon: 'dialog-icon dialog-home-icon',
        routerLink: 'home',
        command: (event) => {
          this.menuItemClick(event);
        },
      },
      {
        label: this.translateService.instant('MERCHANT_MANAGEMENT'),
        icon: 'dialog-icon dialog-shop-icon',
        command: (event) => {
          this.menuItemClick(event);
        },
        visible: this.checkVisibilityMenuItem(
          MODULE_CODE_PERMISSION.MERCHANT_MANAGEMENT,
        ),
        items: [
          {
            label: this.translateService.instant('MANAGE_MERCHANT'),
            routerLink: 'merchant-management-v2/merchant',
            command: (event) => {
              this.menuItemClick(event);
            },
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.MERCHANT_MANAGEMENT.Subsidy_OP_Merchant,
            ),
          },
          {
            label: this.translateService.instant('MANAGE_SUB_MERCHANT'),
            routerLink: 'merchant-management-v2/sub-merchant',
            command: (event) => {
              this.menuItemClick(event);
            },
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.MERCHANT_MANAGEMENT.Subsidy_OP_SubMerchant,
            ),
          },
        ],
      },
      {
        label: this.translateService.instant('ASSET_MANAGEMENT'),
        icon: 'dialog-icon dialog-mobile-icon',
        command: (event) => {
          this.menuItemClick(event);
        },
        visible: this.checkVisibilityMenuItem(
          MODULE_CODE_PERMISSION.ASSET_MANAGEMENT,
        ),
        items: [
          {
            label: this.translateService.instant('MANAGE_ASSET'),
            routerLink: 'asset-management/manage-assets',
            command: (event) => {
              this.menuItemClick(event);
            },
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.ASSET_MANAGEMENT.Subsidy_OP_Asset,
            ),
          },
        ],
      },
      {
        label: this.translateService.instant('WORK_ORDER_MANAGEMENT'),
        icon: 'dialog-icon dialog-folder-minus-icon',
        command: (event) => {
          this.menuItemClick(event);
        },
        routerLink: 'work-order',
        visible: this.checkVisibilityMenuItem(
          MODULE_CODE_PERMISSION.WORK_ORDER_MANAGEMENT.WORK_ORDER,
        ),
      },
      {
        label: this.translateService.instant('REPORTING'),
        icon: 'dialog-icon dialog-status-up-icon',
        command: (event) => {
          this.menuItemClick(event);
        },
        visible: this.checkVisibilityMenuItem(
          MODULE_CODE_PERMISSION.REPORTING.Subsidy_OP_Reporting,
        ),
        routerLink: 'reporting/report-manage',
      },
      {
        label: this.translateService.instant('USER_ACCESS_MANAGEMENT'),
        icon: 'dialog-icon dialog-user-octagon-icon',
        command: (event) => {
          this.menuItemClick(event);
        },
        visible: this.checkVisibilityMenuItem(
          MODULE_CODE_PERMISSION.USER_ACCESS_MANAGEMENT,
        ),
        items: [
          {
            label: this.translateService.instant('USER'),
            command: (event) => {
              this.menuItemClick(event);
            },
            routerLink: 'access-control/users',
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.USER_ACCESS_MANAGEMENT.USER,
            ),
          },
          {
            label: this.translateService.instant('USER_GROUP'),
            command: (event) => {
              this.menuItemClick(event);
            },
            routerLink: 'access-control/user-group',
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.USER_ACCESS_MANAGEMENT.USER_GROUP,
            ),
          },
          {
            label: this.translateService.instant('ROLE_PERMISSIONS'),
            command: (event) => {
              this.menuItemClick(event);
            },
            routerLink: 'access-control/role-permission',
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.USER_ACCESS_MANAGEMENT.ROLE,
            ),
          },
        ],
      },
      {
        label: this.translateService.instant('SETTINGS'),
        icon: 'dialog-icon dialog-setting-2-icon',
        command: (event) => {
          this.menuItemClick(event);
        },
        visible: this.checkVisibilityMenuItem(
          MODULE_CODE_PERMISSION.SETTINGS_TENANT_LEVEL,
        ),
        items: [
          {
            label: this.translateService.instant('GENERAL_SETTINGS'),
            command: (event) => {
              this.menuItemClick(event);
            },
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.SETTINGS_TENANT_LEVEL
                .Subsidy_OP_GeneralSettings,
            ),
            routerLink: 'settings/general-settings',
          },
          {
            label: this.translateService.instant('AUDIT_TRAIL'),
            command: (event) => {
              this.menuItemClick(event);
            },
            visible: this.checkVisibilityMenuItem(
              MODULE_CODE_PERMISSION.SETTINGS_TENANT_LEVEL
                .Subsidy_OP_AuditTrail,
            ),
            routerLink: 'settings/audit-trail',
          },
          {
            label: this.translateService.instant('EVENT_LOG'),
            command: (event) => {
              this.menuItemClick(event);
            },
            routerLink: 'settings/event-log',
          },
        ],
      },
    ];
  }

  // displayPOLevelMenu(): MenuItem[] {
  //   return [
  //     {
  //       label: this.translateService.instant('HOME'),
  //       icon: 'dialog-icon dialog-home-icon',
  //       routerLink: 'home',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //     },
  //     {
  //       label: this.translateService.instant('PROGRAM_OWNER_PROFILE'),
  //       icon: 'dialog-icon dialog-user-square-icon',
  //       routerLink: 'program-owner/profile',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.PROGRAM_OWNER_PROFILE.WMS_OP_POProfile,
  //       ),
  //     },
  //     {
  //       label: this.translateService.instant('DONORS_MANAGEMENT'),
  //       icon: 'dialog-icon dialog-home-icon',
  //       routerLink: 'donor',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.DONOR_MANAGEMENT.WMS_OP_PODonor,
  //       ),
  //     },
  //     {
  //       label: this.translateService.instant('PROGRAM_TYPE'),
  //       icon: 'dialog-icon dialog-clipboard-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       routerLink: 'programme-type',
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.PROGRAM_TYPE.WMS_OP_POProgramType,
  //       ),
  //     },
  //     {
  //       label: this.translateService.instant('PROGRAMME_MANAGEMENT'),
  //       icon: 'dialog-icon dialog-task-square-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.PROGRAM_MANAGEMENT.WMS_OP_POProgramMgmt,
  //       ),
  //       routerLink: 'programme',
  //     },
  //     {
  //       label: this.translateService.instant('RECIPIENT_MANAGEMENT'),
  //       icon: 'dialog-icon dialog-people-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.RECIPIENT_MANAGEMENT.WMS_OP_POManageRecipient,
  //       ),
  //       routerLink: 'recipient-management',
  //     },
  //     {
  //       label: this.translateService.instant('COMMUNITY_MANAGEMENT'),
  //       icon: 'dialog-icon dialog-mobile-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.COMMUNITY_MANAGEMENT
  //           .WMS_OP_POManageCommunities,
  //       ),
  //       routerLink: 'community-management',
  //     },
  //     {
  //       label: this.translateService.instant('CARD_MANAGEMENT'),
  //       icon: 'dialog-icon dialog-personalcard-icon',
  //       routerLink: 'card-management',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.CARD_MANAGEMENT.WMS_OP_PORecipientCard,
  //       ),
  //     },
  //     {
  //       label: this.translateService.instant('APPLICATION_MANAGEMENT'),
  //       icon: 'dialog-icon dialog-mobile-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       // visible: this.checkVisibilityMenuItem(
  //       //   MODULE_CODE_PERMISSION.RECIPIENT_APPLICATION,
  //       // ),
  //       items: [
  //         {
  //           label: this.translateService.instant('APPLICATION_MANAGEMENT'),
  //           command: (event) => {
  //             this.menuItemClick(event);
  //           },
  //           visible: this.checkVisibilityMenuItem(
  //             MODULE_CODE_PERMISSION.RECIPIENT_APPLICATION,
  //           ),
  //           routerLink: 'application-management/application',
  //         },
  //         {
  //           label: this.translateService.instant(
  //             'BULK_APPLICATION_STATUS_UPDATE',
  //           ),
  //           command: (event) => {
  //             this.menuItemClick(event);
  //           },
  //           visible: this.checkVisibilityMenuItem(
  //             MODULE_CODE_PERMISSION.APP_BULK_UPDATE_STATUS,
  //           ),
  //           routerLink: 'application-management/bulk-application-status',
  //         },
  //       ],
  //     },
  //     {
  //       label: this.translateService.instant('REPORTING'),
  //       icon: 'dialog-icon dialog-status-up-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: false,
  //       routerLink: 'reporting',
  //     },
  //     {
  //       label: this.translateService.instant('USER_ACCESS_MANAGEMENT'),
  //       icon: 'dialog-icon dialog-user-octagon-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: false,
  //       items: [
  //         {
  //           label: this.translateService.instant('USER'),
  //           command: (event) => {
  //             this.menuItemClick(event);
  //           },
  //           visible: this.checkVisibilityMenuItem(
  //             MODULE_CODE_PERMISSION.USER_ACCESS_MANAGEMENT.USER,
  //           ),
  //           routerLink: 'access-control/users',
  //         },
  //         {
  //           label: this.translateService.instant('USER_GROUP'),
  //           command: (event) => {
  //             this.menuItemClick(event);
  //           },
  //           visible: this.checkVisibilityMenuItem(
  //             MODULE_CODE_PERMISSION.USER_ACCESS_MANAGEMENT.USER_GROUP,
  //           ),
  //           routerLink: 'access-control/user-group',
  //         },
  //         {
  //           label: this.translateService.instant('ROLE_AND_PERMISSION'),
  //           command: (event) => {
  //             this.menuItemClick(event);
  //           },
  //           visible: this.checkVisibilityMenuItem(
  //             MODULE_CODE_PERMISSION.USER_ACCESS_MANAGEMENT.ROLE,
  //           ),
  //           routerLink: 'access-control/role-permission',
  //         },
  //       ],
  //     },
  //     {
  //       label: this.translateService.instant('SETTINGS'),
  //       icon: 'dialog-icon dialog-setting-2-icon',
  //       command: (event) => {
  //         this.menuItemClick(event);
  //       },
  //       visible: this.checkVisibilityMenuItem(
  //         MODULE_CODE_PERMISSION.SETTINGS_TENANT_LEVEL,
  //       ),
  //       items: [
  //         // {
  //         //   label: this.translateService.instant('SEQUENCE_NO'),
  //         //   command: (event) => {
  //         //     this.menuItemClick(event);
  //         //   },
  //         //   visible: this.checkVisibilityMenuItem(
  //         //     MODULE_CODE_PERMISSION.SETTING_PROGRAM_OWNER_LEVEL
  //         //       .WMS_OP_POSequenceNumber,
  //         //   ),
  //         //   routerLink: 'settings/sequence-no',
  //         // },
  //         // {
  //         //   label: this.translateService.instant('CUSTOM_FIELD'),
  //         //   command: (event) => {
  //         //     this.menuItemClick(event);
  //         //   },
  //         //   routerLink: 'settings/custom-field',
  //         //   visible: this.checkVisibilityMenuItem(
  //         //     MODULE_CODE_PERMISSION.SETTING_PROGRAM_OWNER_LEVEL
  //         //       .WMS_OP_POCustomField,
  //         //   ),
  //         // },
  //         // {
  //         //   label: this.translateService.instant('APPROVAL_FLOW'),
  //         //   command: (event) => {
  //         //     this.menuItemClick(event);
  //         //   },
  //         //   routerLink: 'settings/approval-flow',
  //         //   visible: this.checkVisibilityMenuItem(
  //         //     MODULE_CODE_PERMISSION.SETTING_PROGRAM_OWNER_LEVEL
  //         //       .WMS_OP_POApprovalFlow,
  //         //   ),
  //         // },
  //         {
  //           label: this.translateService.instant('GENERAL_SETTINGS'),
  //           command: (event) => {
  //             this.menuItemClick(event);
  //           },
  //           visible: this.checkVisibilityMenuItem(
  //             MODULE_CODE_PERMISSION.SETTINGS_TENANT_LEVEL
  //               .Subsidy_OP_GeneralSettings,
  //           ),
  //           routerLink: 'settings/general-settings',
  //         },
  //       ],
  //     },
  //   ];
  // }

  private checkVisibilityMenuItem(moduleNameList: string | object) {
    return !!this.roleService.getActionsByModuleCode(moduleNameList);
  }
}
